import {useExpertSystemHierarchy} from '@hconnect/common/hooks'
import {Iso8601} from '@hconnect/common/types'
import {findNodeInHierarchy} from '@hconnect/common/utils'
import {useCallback, useMemo} from 'react'

import {useExpertSystemUsage} from './api'

type Params = {
  hierarchyId: string
  from?: Iso8601
  to?: Iso8601
}

export const useExpertSystemEquipmentsWithNames = (
  params: Params,
  options?: {enabled: boolean}
) => {
  const {
    data: structure,
    error: structureError,
    isLoading: structureLoading,
    isFetching: structureFetching,
    isRefetching: structureRefetching,
    refetch: structureRefetch
  } = useExpertSystemHierarchy(options)
  const {
    data: expertSystemEquipments,
    error: expertSystemEquipmentsError,
    isLoading: expertSystemEquipmentsLoading,
    isFetching: expertSystemEquipmentsFetching,
    isRefetching: expertSystemEquipmentsRefetching,
    refetch: expertSystemEquipmentsRefetch
  } = useExpertSystemUsage(params, options)

  const expertSystemEquipmentsWithNames = useMemo(() => {
    if (!structure || !expertSystemEquipments) return undefined
    const equipments = findNodeInHierarchy(params.hierarchyId, structure)?.children
    return expertSystemEquipments.map((equipment) => {
      const equipmentNode = equipments?.find((node) => node.id === equipment.hierarchyId)
      return {
        ...equipment,
        name: equipmentNode?.name || equipment.hierarchyId
      }
    })
  }, [expertSystemEquipments, params.hierarchyId, structure])

  const refetch = useCallback(() => {
    if (structureError) void structureRefetch()
    if (expertSystemEquipmentsError) void expertSystemEquipmentsRefetch()
  }, [expertSystemEquipmentsError, expertSystemEquipmentsRefetch, structureError, structureRefetch])

  return useMemo(
    () => ({
      data: expertSystemEquipmentsWithNames,
      isLoading: structureLoading || expertSystemEquipmentsLoading,
      isFetching: structureFetching || expertSystemEquipmentsFetching,
      error: structureError || expertSystemEquipmentsError,
      isError: !!(structureError || expertSystemEquipmentsError),
      isRefetching: structureRefetching || expertSystemEquipmentsRefetching,
      refetch
    }),
    [
      expertSystemEquipmentsError,
      expertSystemEquipmentsFetching,
      expertSystemEquipmentsLoading,
      expertSystemEquipmentsRefetching,
      expertSystemEquipmentsWithNames,
      refetch,
      structureError,
      structureFetching,
      structureLoading,
      structureRefetching
    ]
  )
}
